import React, { useEffect, useRef, useState } from "react";

export default function OtpFields({ length = 4, value, onChange }: { length?: number; value: string; onChange: (v: string) => void }) {
    const inputsRef = useRef<HTMLInputElement[]>([]);
    const [active, setActive] = useState<Record<number, boolean>>({});

    useEffect(() => {
        if (inputsRef.current[0] && !value) inputsRef.current[0].focus();
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
        const val = e.target.value.replace(/\D/g, "").slice(0, 1);
        const arr = Array.from({ length }).map((_, i) => value[i] ?? "");
        arr[idx] = val;
        const newVal = arr.join("").slice(0, length);
        onChange(newVal);

        // trigger brief pop animation
        setActive(prev => ({ ...prev, [idx]: true }));
        window.setTimeout(() => setActive(prev => { const n = { ...prev }; delete n[idx]; return n; }), 220);

        if (val && inputsRef.current[idx + 1]) inputsRef.current[idx + 1].focus();
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
        const key = e.key;
        if (key === "Backspace") {
            if (!value[idx] && inputsRef.current[idx - 1]) {
                const arr = Array.from({ length }).map((_, i) => value[i] ?? "");
                arr[idx - 1] = "";
                onChange(arr.join("").slice(0, length));
                inputsRef.current[idx - 1].focus();
            } else {
                const arr = Array.from({ length }).map((_, i) => value[i] ?? "");
                arr[idx] = "";
                onChange(arr.join("").slice(0, length));
            }
        } else if (key === "ArrowLeft" && inputsRef.current[idx - 1]) {
            inputsRef.current[idx - 1].focus();
        } else if (key === "ArrowRight" && inputsRef.current[idx + 1]) {
            inputsRef.current[idx + 1].focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        const paste = e.clipboardData.getData("Text").replace(/\D/g, "").slice(0, length);
        if (!paste) return;
        const arr = Array.from({ length }).map((_, i) => paste[i] ?? "");
        const newVal = arr.join("").slice(0, length);
        onChange(newVal);
        // focus after paste
        const nextIdx = Math.min(paste.length, length - 1);
        if (inputsRef.current[nextIdx]) inputsRef.current[nextIdx].focus();
        e.preventDefault();
    };

    return (
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 6 }}>
            {Array.from({ length }).map((_, i) => {
                const filled = !!value[i];
                const isActive = !!active[i];
                return (
                    <input
                        key={i}
                        ref={(el) => { if (el) inputsRef.current[i] = el; }}
                        value={value[i] || ""}
                        onChange={(e) => handleChange(e, i)}
                        onKeyDown={(e) => handleKeyDown(e, i)}
                        onPaste={handlePaste}
                        inputMode="numeric"     // mobile numeric keyboard
                        pattern="\d*"
                        type="text"             // avoid spinner arrows
                        maxLength={1}
                        aria-label={`Código ${i + 1}`}
                        onFocus={(e) => e.currentTarget.select()}
                        style={{
                            width: 64,
                            height: 64,
                            textAlign: "center",
                            fontSize: 28,
                            borderRadius: 10,
                            border: "1px solid rgba(0,0,0,0.12)",
                            background: filled ? "#fff" : "transparent",
                            transition: "transform 120ms ease, box-shadow 160ms ease, border-color 120ms ease",
                            transform: isActive ? "scale(1.06)" : "scale(1)",
                            boxShadow: (isActive || filled) ? "0 10px 30px rgba(0,112,244,0.12)" : undefined,
                            outline: "none",
                        }}
                    />
                );
            })}
        </div>
    );
}